import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { down } from '../../../styles/media';
import {
  ButtonCustomBackground,
  ButtonCustomTransparent,
} from '../../ButtonCustom';

export const HeaderBox = styled.header``;

export const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: clamp(0.5rem, 1.7vw, 1.5rem);
  height: clamp(4rem, 5vw + 1.5rem, 5.3125rem);
  padding: 0 clamp(1rem, 3.5vw, 3.125rem);
`;

/* Faixa verde que fecha o header (antes era um border-bottom de 1px que só
   aparecia nas páginas com título). */
export const HeaderAccent = styled.div`
  height: 0.1875rem;
  background: var(--second-color);
`;

export const SearchBox = styled.form`
  width: clamp(10rem, 18vw, 17.5rem);
  flex-shrink: 0;
`;

export const LogoImg = styled.img`
  height: 2.75rem;
  width: auto;
`;
export const ButtonsBox = styled.div`
  align-items: center;
  display: flex;
  gap: clamp(0.5rem, 1.4vw, 1.2rem);
  flex-shrink: 0;
  min-width: 0;
`;

export const SecondNav = styled.nav`
  display: flex;
  align-items: center;
  /* a nav cresce para ocupar o espaço entre logo e botões; o center mantém
     os itens no meio desse espaço em vez de encostados no logo */
  justify-content: center;
  height: 100%;
  gap: clamp(0.5rem, 2vw, 1.8rem);
  /* min-width: 0 é o que permite os itens encolherem; sem ele o flex mantém
     a largura de conteúdo e empurra a busca para fora da viewport. */
  min-width: 0;
  flex: 1 1 auto;
`;

export const HomeLink = styled(NavLink)`
  display: flex;
  align-items: center;
  font-size: clamp(0.75rem, 0.35vw + 0.63rem, 0.9375rem);
  font-weight: bold;
  white-space: nowrap;
  text-decoration: none;
  position: relative;
  transition: color 0.2s ease-in-out;

  &&,
  &&:visited {
    color: var(--first-color);
  }

  &&.active,
  &&.active:visited {
    color: var(--second-color);
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: var(--sixth-color);
    transition: width 0.2s ease-in-out;
  }

  &:hover {
    color: var(--second-color);
  }

  &:hover::after {
    width: 100%;
  }
`;

export const MenuButton = styled.button`
  color: ${({ $active }) =>
    $active ? 'var(--second-color)' : 'var(--first-color)'};
  display: flex;
  align-items: center;
  font-size: clamp(0.75rem, 0.35vw + 0.63rem, 0.9375rem);
  font-weight: bold;
  white-space: nowrap;
  /* Altura do próprio texto (o MenuItem é que ocupa a altura do header), para
     o underline continuar logo abaixo da palavra. */
  height: auto;

  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease-in-out;

  /* Underline: aparece apenas no hover; o estado ativo fica só com a cor */
  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: var(--sixth-color);
    transition: width 0.2s ease-in-out;
  }

  &:hover {
    color: var(--second-color);
  }

  &:hover::after {
    width: 100%;
  }
`;

/* Área de hover de cada item: ocupa toda a altura do header e avança para os
   lados dentro do gap (padding + margem negativa de mesmo valor, para o
   espaçamento entre os itens não mudar). Sem isso o dropdown só abria com o
   ponteiro exatamente sobre a palavra. */
export const MenuItem = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding-inline: clamp(0.25rem, 1vw, 0.9rem);
  margin-inline: calc(-1 * clamp(0.25rem, 1vw, 0.9rem));

  /* A cor e o underline acompanham a área inteira, não só a palavra -- vale
     para o botão dos itens com dropdown e para o link do Início. */
  &:hover ${MenuButton},
  &:hover ${HomeLink} {
    color: var(--second-color);
  }

  &:hover ${HomeLink}::after {
    width: 100%;
  }

  &:hover ${MenuButton}::after {
    width: 100%;
  }
`;

export const DropdownWrapper = styled.div`
  position: absolute;
  top: 100%;
  /* left/right: 0 + margin-inline: auto centraliza dentro do HeaderRow (que é
     position: relative) sem o translateX(-50%), que ignorava a viewport e
     fazia o painel vazar para fora da tela em telas estreitas. */
  left: 0;
  right: 0;
  margin-inline: auto;
  padding: clamp(1rem, 2vw, 2rem);
  width: max-content;
  max-width: min(54rem, calc(100% - 2rem));
  background: var(--white-color);
  border-top: 4px solid var(--second-color);
  border-radius: 0 0 0.75rem 0.75rem;
  box-shadow: 0 0.9375rem 2.1875rem rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

export const ButtonBackground = styled(ButtonCustomBackground)``;

export const ButtonTransparent = styled(ButtonCustomTransparent)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2.375rem;
  box-sizing: border-box;
  padding: 0 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.625rem;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease,
    transform 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 1.125rem;
    height: 1.125rem;
    flex-shrink: 0;
  }

  /* Entre lg e xl o header fica apertado: o botão vira só o ícone. */
  ${down('xl')} {
    padding: 0 0.65rem;
    gap: 0;

    span {
      display: none;
    }

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  &:hover {
    background-color: var(--second-color);
    color: var(--white-color);
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 137, 58, 0.2);
  }
`;
